import dayjs from 'dayjs';
import { concat, isUndefined } from 'lodash';
import { getFormattedDate, sortObjByKey } from 'utils/services/common.service';
import {
  jobChatHistoryApi,
  seenMessagesApi,
  sendMessageApi,
  subscribeChatApi,
} from './api';
import { ChatMessage, SendMessage, TimedMessages } from './types';

/**
 * A chat timestamp as "Today", "Yesterday", or the date.
 *
 * Day-of-month arithmetic cannot answer this. The previous version compared
 * getDate() values, so on the 1st of a month yesterday's number is the LARGER
 * one: on 1 October it worked out 1 - 30 = -29, matched neither branch, and
 * labelled yesterday's messages "Sep 30 2026". The same happened on 1 January
 * for the year. dayjs compares whole calendar days, which is the actual
 * question being asked.
 *
 * `now` defaults to the current time and exists so the month and year
 * boundaries can be tested without mocking the clock - the bug above hid for
 * months precisely because a test relative to "now" only exercises the day it
 * runs on.
 */
export const getFormattedChatTime = (chatTime: string, now = dayjs()) => {
  const chatDate = dayjs(chatTime);

  if (chatDate.isSame(now, 'day')) return 'Today';
  if (chatDate.isSame(now.subtract(1, 'day'), 'day')) return 'Yesterday';

  return getFormattedDate(chatTime);
};

export const getJobChatHistory = async (jobId, isHistory) => {
  return await jobChatHistoryApi(jobId, isHistory);
};

export const postChatMessage = async (jobId, message) => {
  const payload: SendMessage = {
    jobId,
    message,
  };
  return await sendMessageApi(payload);
};

export const subscribeChat = async jobId => {
  return await subscribeChatApi(jobId);
};

export const convertStreamedMessageToViewType = (
  messages: TimedMessages,
  incomingMessage: ChatMessage,
) => {
  const today = getFormattedDate(new Date().toString(), 'YYYY-MM-DD');
  if (isUndefined(messages[today])) messages[today] = [];
  messages[today].push({
    ...incomingMessage,
  });
  return messages;
};

export const seenMessages = async (messageIds: string[], jobId: string) => {
  return await seenMessagesApi({ messageIds }, jobId);
};

export const loadMoreMessages = async (
  jobId: string,
  isHistory: boolean,
  messageId: string,
) => {
  return await jobChatHistoryApi(jobId, isHistory, messageId);
};

export const addOlderMessages = (
  messages: TimedMessages,
  olderMessages: TimedMessages,
): TimedMessages => {
  for (const oldMessageDate in olderMessages) {
    if (!isUndefined(messages[oldMessageDate])) {
      messages[oldMessageDate] = concat(
        olderMessages[oldMessageDate],
        messages[oldMessageDate],
      );
    } else {
      messages[oldMessageDate] = [...olderMessages[oldMessageDate]];
    }
  }
  return sortObjByKey(messages);
};
